/**
 * FinanceReports.jsx — FM6 Finance Reports + Analytics Workspace
 * Route: /finance/reports
 * Authorization: FM6_IMPLEMENTATION_PLAN.md v2 — APPROVED
 *
 * RBAC (frontend hints — backend is authoritative):
 *   finance.reports.executive  → Super Admin, Institution Admin, CFO, Finance Manager
 *   finance.reports.export     → Super Admin, Institution Admin, CFO, Finance Manager
 *   finance.reports.view       → all above + Finance Officer, Registrar
 *
 * Invariants enforced in UI:
 *   INV-FM6-RBAC-01:   overview tab hidden from non-executive roles
 *   INV-FM6-DEFAULTER-01: defaulter_count=NOT_CONNECTED always shown
 *   INV-FM6-04:  aging provenance split — AUTHORITATIVE / MIXED / NOT_AVAILABLE
 *   INV-FM6-07:  CSV export carries same semantics and provenance as screen
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import {
  BarChart2, FileText, DollarSign, BookOpen, Users, RefreshCw,
  Download, ChevronRight, AlertCircle, Lock, FileWarning, BadgeInfo,
  TrendingUp, TrendingDown, Activity, Archive, ShieldOff, CreditCard,
  Package, Receipt, Scale, Database,
} from "lucide-react";
// FM7 BOUND-REOPEN-FM7-04: Export to ERP capability gate (ACTIVE ERP integration required)
import { useIntegrationStatus } from "../hooks/useIntegrationStatus";

// ── RBAC role sets (INV-FM6-RBAC-01) ──────────────────────────────────────────
const EXECUTIVE_ROLES = new Set(["super_admin", "institution_admin", "cfo", "finance_manager"]);
const EXPORT_ROLES    = new Set(["super_admin", "institution_admin", "cfo", "finance_manager"]);
const VIEW_ROLES      = new Set([...EXECUTIVE_ROLES, "finance_officer", "registrar"]);

// ── Formatters ─────────────────────────────────────────────────────────────────
const fmtAmt = (v, cur) => {
  const n = parseFloat(v ?? 0);
  const sym = cur === "USD" ? "$" : cur === "AED" ? "AED " : "₹";
  return isNaN(n) ? `${sym}0.00` : `${sym}${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
};
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const badge = (v, map) => {
  const cfg = map?.[v] ?? { cls: "bg-slate-700 text-slate-300", label: v || "—" };
  return <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${cfg.cls}`}>{cfg.label}</span>;
};

const STATUS_COLORS = {
  FINALIZED: { cls: "bg-emerald-900/50 text-emerald-300", label: "Finalized" },
  DRAFT:     { cls: "bg-slate-700 text-slate-300",        label: "Draft" },
  CANCELLED: { cls: "bg-red-900/50 text-red-300",         label: "Cancelled" },
  ACTIVE:    { cls: "bg-emerald-900/50 text-emerald-300", label: "Active" },
  POSTED:    { cls: "bg-blue-900/50 text-blue-300",       label: "Posted" },
  PENDING:   { cls: "bg-amber-900/50 text-amber-300",     label: "Pending" },
  APPLIED:   { cls: "bg-emerald-900/50 text-emerald-300", label: "Applied" },
  APPROVED:  { cls: "bg-blue-900/50 text-blue-300",       label: "Approved" },
  INITIATED: { cls: "bg-amber-900/50 text-amber-300",     label: "Initiated" },
  UNDER_REVIEW: { cls: "bg-violet-900/50 text-violet-300", label: "Under Review" },
  REJECTED:  { cls: "bg-red-900/50 text-red-300",         label: "Rejected" },
  EXECUTED:  { cls: "bg-emerald-900/50 text-emerald-300", label: "Executed" },
  OPEN:      { cls: "bg-red-900/50 text-red-300",         label: "Open" },
  MONITORING:{ cls: "bg-amber-900/50 text-amber-300",     label: "Monitoring" },
  DEFAULTED: { cls: "bg-red-900/60 text-red-200 font-bold", label: "Defaulted" },
  SUSPENDED: { cls: "bg-slate-700 text-slate-300",        label: "Suspended" },
  RESOLVED:  { cls: "bg-emerald-900/50 text-emerald-300", label: "Resolved" },
};
const PROV_COLORS = {
  AUTHORITATIVE: { cls: "bg-emerald-900/50 text-emerald-300", label: "Authoritative" },
  NOT_AVAILABLE: { cls: "bg-amber-900/50 text-amber-300",     label: "Provenance Unverified" },
  MIXED:         { cls: "bg-blue-900/50 text-blue-300",       label: "Mixed" },
};

// ── Shared table ───────────────────────────────────────────────────────────────
function ReportTable({ cols, rows, emptyMsg = "No records found." }) {
  if (!rows?.length) return (
    <div className="flex items-center justify-center h-32 text-slate-500 text-sm" data-testid="report-table-empty">{emptyMsg}</div>
  );
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-700/60" data-testid="report-table">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-800/80">
          <tr>{cols.map(c => <th key={c.key} className="px-3 py-2.5 text-left text-xs font-semibold text-slate-400 whitespace-nowrap">{c.label}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-700/40">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-slate-800/40 transition-colors">
              {cols.map(c => <td key={c.key} className="px-3 py-2 text-slate-300 whitespace-nowrap">{c.render ? c.render(row) : (row[c.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Not-available state ────────────────────────────────────────────────────────
function NotAvailable({ label = "NOT_AVAILABLE", note }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-900/20 border border-amber-700/40 rounded text-amber-400 text-xs font-semibold" data-testid="kpi-not-available">
      <BadgeInfo className="h-3.5 w-3.5 shrink-0" />{label}{note && <span className="font-normal text-amber-500 ml-1">— {note}</span>}
    </div>
  );
}

// ── KPI card ───────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon: Icon, avail = true, testid }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 flex flex-col gap-2" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-wide">
        {Icon && <Icon className="h-4 w-4" />}{label}
      </div>
      {avail ? (
        <div className="text-xl font-bold text-white truncate">{value ?? "—"}</div>
      ) : (
        <NotAvailable />
      )}
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

// ── Pagination ─────────────────────────────────────────────────────────────────
function Pager({ offset, limit, total, onChange }) {
  const page = Math.floor(offset / limit);
  const pages = Math.ceil(total / limit);
  if (pages <= 1) return null;
  return (
    <div className="flex items-center gap-3 text-sm text-slate-400 mt-2" data-testid="report-pager">
      <button disabled={page === 0} onClick={() => onChange((page - 1) * limit)} className="px-2 py-1 rounded bg-slate-700 disabled:opacity-40 hover:bg-slate-600">Prev</button>
      <span>Page {page + 1} / {pages} &nbsp;·&nbsp; {total} records</span>
      <button disabled={page >= pages - 1} onClick={() => onChange((page + 1) * limit)} className="px-2 py-1 rounded bg-slate-700 disabled:opacity-40 hover:bg-slate-600">Next</button>
    </div>
  );
}

// ── CSV download helper (INV-FM6-07) ───────────────────────────────────────────
async function triggerCsvDownload(endpoint, params = {}) {
  const token = localStorage.getItem("academiaos_token");
  const qs = new URLSearchParams(params).toString();
  const url = `${API}/finance/reports/${endpoint}/export${qs ? "?" + qs : ""}`;
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error(`Export failed: ${res.status}`); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `fm6_${endpoint}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV export started");
  } catch {
    toast.error("Export request failed");
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// SECTION COMPONENTS
// ══════════════════════════════════════════════════════════════════════════════

// ── Overview (executive only) ──────────────────────────────────────────────────
function OverviewSection({ iid, canExec }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canExec || !iid) return;
    setLoading(true);
    api.get(`/finance/reports/overview?institution_id=${iid}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Overview failed"))
      .finally(() => setLoading(false));
  }, [iid, canExec]);

  if (!canExec) return (
    <div className="flex items-center gap-3 p-6 bg-slate-800/40 rounded-xl border border-slate-700/50" data-testid="overview-locked">
      <Lock className="h-5 w-5 text-slate-500 shrink-0" />
      <div>
        <p className="text-sm font-semibold text-slate-300">Executive Access Required</p>
        <p className="text-xs text-slate-500 mt-0.5">finance.reports.executive — Finance Officer and Registrar roles cannot access the executive overview. (INV-FM6-RBAC-01)</p>
      </div>
    </div>
  );

  const d = data?.data;
  const ca = d?.class_a_kpis;
  const cb = d?.class_b_kpis;

  return (
    <div className="space-y-6" data-testid="overview-section">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest">Executive Finance Overview</h2>
        {loading && <RefreshCw className="h-4 w-4 text-slate-500 animate-spin" />}
        {ca?.period && (
          <span className="text-xs bg-blue-900/30 text-blue-300 border border-blue-800/40 px-2 py-1 rounded-full">
            {ca.period.label} · {fmtDate(ca.period.start_date)} — {fmtDate(ca.period.end_date)}
          </span>
        )}
      </div>

      {/* FK1 Defaulter Count: NOT_CONNECTED — always visible */}
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/40 border border-slate-700/40 rounded-lg text-xs text-slate-400" data-testid="defaulter-count-not-connected">
        <ShieldOff className="h-3.5 w-3.5 text-slate-500" />
        <span>FK1 Institutional Defaulter Count: <strong className="text-amber-400">NOT_CONNECTED</strong> — FM6 does not reconnect or redefine the frozen FK1 Defaulter Count. (INV-FM6-DEFAULTER-01)</span>
      </div>

      {/* Class A KPIs */}
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Class A — Frozen FK1 Metrics</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Fees Billed" icon={DollarSign} testid="kpi-fees-billed"
            avail={ca?.fiscal_period_status === "CURRENT" && ca.fees_billed !== null}
            value={ca?.fees_billed !== null ? fmtAmt(ca?.fees_billed) : null}
            sub={ca?.fiscal_period_status !== "CURRENT" ? "No active fiscal period" : undefined} />
          <KpiCard label="Net Collectible" icon={Scale} testid="kpi-net-collectible"
            avail={ca?.net_collectible !== null}
            value={ca?.net_collectible !== null ? fmtAmt(ca?.net_collectible) : null} />
          <KpiCard label="Fees Collected" icon={TrendingUp} testid="kpi-fees-collected"
            avail={ca?.fees_collected !== null}
            value={ca?.fees_collected !== null ? fmtAmt(ca?.fees_collected) : null} />
          <KpiCard label="Collection Rate" icon={Activity} testid="kpi-collection-rate"
            avail={ca?.collection_rate != null}
            value={ca?.collection_rate != null ? `${ca?.collection_rate}%` : null}
            sub={ca?.collection_rate == null ? "Net Collectible = 0" : undefined} />
        </div>
      </div>

      {/* Class B KPIs */}
      {cb && (
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Class B — FM6 Operational Metrics</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KpiCard label="Student Collectible Outstanding" icon={TrendingDown} testid="kpi-b01"
              avail={cb.student_collectible_outstanding?.availability === "AVAILABLE"}
              value={cb.student_collectible_outstanding?.availability === "AVAILABLE"
                ? cb.student_collectible_outstanding.by_currency?.map(c => `${fmtAmt(c.collectible, c.currency)} ${c.currency}`).join(" · ")
                : null}
              sub="Excludes sponsor + govt covered amounts; PTP not deducted" />
            <KpiCard label="Sponsor Receivable" icon={Users} testid="kpi-b02"
              avail={cb.sponsor_receivable?.availability === "AVAILABLE"}
              value={cb.sponsor_receivable?.by_currency?.map(c => `${fmtAmt(c.receivable, c.currency)} ${c.currency}`).join(" · ")} />
            <KpiCard label="Government Receivable" icon={Archive} testid="kpi-b03"
              avail={cb.government_receivable?.availability === "AVAILABLE"}
              value={cb.government_receivable?.availability === "AVAILABLE" ? fmtAmt(cb.government_receivable.receivable) : null} />
            <KpiCard label="Refunds Pending" icon={Receipt} testid="kpi-b04"
              avail={cb.refunds_pending?.availability === "AVAILABLE"}
              value={cb.refunds_pending?.availability === "AVAILABLE"
                ? `${cb.refunds_pending.pending_count} · ${fmtAmt(cb.refunds_pending.pending_amount)}` : null}
              sub="INITIATED + UNDER_REVIEW + APPROVED (not yet disbursed)" />
            <KpiCard label="Authoritative Overdue" icon={AlertCircle} testid="kpi-b05"
              avail={cb.authoritative_overdue_amount?.availability === "AVAILABLE"}
              value={cb.authoritative_overdue_amount?.availability === "AVAILABLE"
                ? fmtAmt(cb.authoritative_overdue_amount.total_overdue) : null}
              sub="AUTHORITATIVE provenance only. Provenance-unverified excluded." />
            <KpiCard label="Open Collection Cases" icon={Activity} testid="kpi-b07"
              avail={cb.open_collection_cases?.availability === "AVAILABLE"}
              value={cb.open_collection_cases?.availability === "AVAILABLE"
                ? `${cb.open_collection_cases.total} cases` : null}
              sub="NOT the FK1 Defaulter Count (INV-FM6-DEFAULTER-01)" />
          </div>
        </div>
      )}

      {/* Credits by origin */}
      {cb?.credits_by_origin?.availability === "AVAILABLE" && (
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4" data-testid="kpi-b06-credits">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Credits by Origin (B-06) — Never aggregated as discount</p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-500 mb-1">FM4A Origin (Scholarship / Waiver)</p>
              <p className="text-lg font-bold text-blue-300">{fmtAmt(cb.credits_by_origin.fm4a_origin.total)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">FM5 Origin (Governed Credit)</p>
              <p className="text-lg font-bold text-violet-300">{fmtAmt(cb.credits_by_origin.fm5_origin.total)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Generic paginated report ───────────────────────────────────────────────────
function GenericReport({ endpoint, cols, params = {}, canExport, exportLabel, iid, extraFilters, testid }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(params);
  const LIMIT = 50;

  const load = useCallback(() => {
    if (!iid) return;
    setLoading(true);
    const qs = new URLSearchParams({ institution_id: iid, limit: LIMIT, offset, ...filters })
      .toString();
    api.get(`/finance/reports/${endpoint}?${qs}`)
      .then(r => { setRows(r.data?.data?.rows || []); setTotal(r.data?.data?.total || 0); })
      .catch(e => toast.error(e?.response?.data?.detail || `${endpoint} failed`))
      .finally(() => setLoading(false));
  }, [endpoint, iid, offset, filters]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    const p = { institution_id: iid, ...filters };
    triggerCsvDownload(endpoint, p);
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {extraFilters && <div className="flex gap-2">{extraFilters(filters, setFilters)}</div>}
        <div className="ml-auto flex items-center gap-2">
          {loading && <RefreshCw className="h-4 w-4 text-slate-500 animate-spin" />}
          {canExport && (
            <button onClick={handleExport} data-testid={`${testid}-export-btn`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium transition-colors">
              <Download className="h-3.5 w-3.5" />{exportLabel || "Export CSV"}
            </button>
          )}
        </div>
      </div>
      <ReportTable cols={cols} rows={rows} />
      <Pager offset={offset} limit={LIMIT} total={total} onChange={setOffset} />
    </div>
  );
}

// ── Aging section (INV-FM6-04) ─────────────────────────────────────────────────
function AgingSection({ iid, canExport }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!iid) return;
    setLoading(true);
    api.get(`/finance/reports/aging?institution_id=${iid}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Aging report failed"))
      .finally(() => setLoading(false));
  }, [iid]);

  const auth = data?.data?.authoritative || [];
  const unv  = data?.data?.provenance_unverified || [];

  const agingCols = [
    { key: "id",                       label: "Case ID",      render: r => <span className="font-mono text-xs text-slate-500">{r.id?.slice(0,8)}…</span> },
    { key: "student_account_id",       label: "Student",      render: r => <span className="font-mono text-xs">{r.student_account_id?.slice(0,8)}…</span> },
    { key: "status",                   label: "Status",       render: r => badge(r.status, STATUS_COLORS) },
    { key: "aging_provenance",         label: "Provenance",   render: r => badge(r.aging_provenance, PROV_COLORS) },
    { key: "aging_bucket",             label: "Bucket" },
    { key: "collectible_amount_at_open", label: "Collectible", render: r => fmtAmt(r.collectible_amount_at_open) },
    { key: "provenance_unverified_amount", label: "Unverified Amt",
      render: r => r.provenance_unverified_amount > 0
        ? <span className="text-amber-400">{fmtAmt(r.provenance_unverified_amount)}</span>
        : <span className="text-slate-600">—</span>
    },
    { key: "overdue_since", label: "Overdue Since", render: r => fmtDate(r.overdue_since) },
  ];

  return (
    <div className="space-y-5" data-testid="aging-section">
      <div className="flex items-center justify-between">
        {loading && <RefreshCw className="h-4 w-4 text-slate-500 animate-spin" />}
        {canExport && (
          <button onClick={() => triggerCsvDownload("aging", { institution_id: iid })}
            data-testid="aging-export-btn"
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-xs font-medium">
            <Download className="h-3.5 w-3.5" />Export Aging CSV
          </button>
        )}
      </div>

      {/* INV-FM6-DEFAULTER-01 notice */}
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-800/40 border border-slate-700/40 rounded-lg text-xs text-slate-400" data-testid="aging-defaulter-not-connected">
        <ShieldOff className="h-3.5 w-3.5 text-slate-500 shrink-0" />
        FK1 Institutional Defaulter Count: <strong className="text-amber-400 ml-1">NOT_CONNECTED</strong>
        <span className="ml-1">— (INV-FM6-DEFAULTER-01)</span>
      </div>

      {/* Authoritative / MIXED rows */}
      <div data-testid="aging-authoritative-block">
        <p className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">
          Authoritative &amp; Mixed Provenance ({auth.length} cases)
        </p>
        {auth.length === 0
          ? <div className="text-slate-500 text-sm p-4 bg-slate-800/30 rounded-lg">No authoritative aging cases.</div>
          : <ReportTable cols={agingCols} rows={auth} />
        }
        {auth.some(r => r.aging_provenance === "MIXED") && (
          <div className="flex items-start gap-2 mt-2 p-3 bg-blue-900/20 border border-blue-700/40 rounded-lg text-xs text-blue-300">
            <BadgeInfo className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>MIXED provenance: authoritative portion shown in bucket; <strong>provenance_unverified_amount is NOT placed in any aging bucket</strong> (INV-FM6-04).</span>
          </div>
        )}
      </div>

      {/* Provenance-unverified rows */}
      <div data-testid="aging-unverified-block">
        <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">
          Provenance Unverified — NOT Overdue ({unv.length} cases)
        </p>
        {unv.length > 0 && (
          <div className="flex items-start gap-2 mb-2 p-3 bg-amber-900/20 border border-amber-700/40 rounded-lg text-xs text-amber-300">
            <FileWarning className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              <strong>PROVENANCE_UNVERIFIED ≠ overdue ≠ default ≠ collectible aging bucket.</strong> These cases have surrogate migration due dates (fee_assessment_id IS NULL).
              No delinquency conclusion can be drawn. (INV-FM6-04)
            </span>
          </div>
        )}
        {unv.length === 0
          ? <div className="text-slate-500 text-sm p-4 bg-slate-800/30 rounded-lg">No provenance-unverified cases.</div>
          : <ReportTable cols={agingCols} rows={unv} />
        }
      </div>
    </div>
  );
}

// ── Export Centre ──────────────────────────────────────────────────────────────
function ExportCentre({ iid, canExport }) {
  if (!canExport) return (
    <div className="flex items-center gap-3 p-6 bg-slate-800/40 rounded-xl border border-slate-700/50" data-testid="export-centre-locked">
      <Lock className="h-5 w-5 text-slate-500 shrink-0" />
      <div>
        <p className="text-sm font-semibold text-slate-300">Export Access Required</p>
        <p className="text-xs text-slate-500 mt-0.5">finance.reports.export — Finance Officer and Registrar roles do not have export permission. (INV-FM6-RBAC-01)</p>
      </div>
    </div>
  );

  const exports = [
    { ep: "assessments",   label: "Assessments",         icon: FileText,  desc: "All charge/assessment records" },
    { ep: "invoices",      label: "Invoices",            icon: Receipt,   desc: "Invoice history (immutable)" },
    { ep: "payments",      label: "Payments",            icon: CreditCard, desc: "Student payments (FM3)" },
    { ep: "scholarships",  label: "Scholarships",        icon: BookOpen,  desc: "Scholarship awards (FM4A)" },
    { ep: "sponsorships",  label: "Sponsorships",        icon: Users,     desc: "Sponsorship assignments (FM4B)" },
    { ep: "reimbursements",label: "Reimbursements",      icon: Archive,   desc: "Government reimbursements (FM4C)" },
    { ep: "refunds",       label: "Refunds",             icon: TrendingDown, desc: "Refund requests (FM5)" },
    { ep: "credits",       label: "Credits",             icon: Scale,     desc: "Credit memos with FM4A/FM5 origin (FM5)" },
    { ep: "collections",   label: "Collections",         icon: AlertCircle, desc: "Collection cases (FM5)" },
    { ep: "aging",         label: "Aging",               icon: Activity,  desc: "Provenance-aware aging split" },
  ];

  return (
    <div className="space-y-4" data-testid="export-centre">
      <div className="flex items-start gap-2 p-3 bg-blue-900/20 border border-blue-700/40 rounded-lg text-xs text-blue-300">
        <BadgeInfo className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>All exports carry: <strong>generated_at, report_version, correlation_id, filters_applied, currency_context</strong>. Screen/export semantic parity guaranteed for the same snapshot context. (INV-FM6-07)</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {exports.map(({ ep, label, icon: Icon, desc }) => (
          <button key={ep} data-testid={`export-btn-${ep}`}
            onClick={() => triggerCsvDownload(ep, { institution_id: iid })}
            className="flex items-center gap-3 p-4 bg-slate-800/60 border border-slate-700/50 rounded-xl hover:bg-slate-700/60 hover:border-slate-600/60 transition-all text-left group">
            <div className="flex-shrink-0 p-2 bg-slate-700 rounded-lg group-hover:bg-slate-600">
              <Icon className="h-4 w-4 text-slate-300" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-200">{label}</p>
              <p className="text-xs text-slate-500">{desc}</p>
            </div>
            <Download className="h-4 w-4 text-slate-500 ml-auto group-hover:text-slate-300 transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function FinanceReports() {
  const { user } = useAuth();
  const canExec   = EXECUTIVE_ROLES.has(user?.role);
  const canExport = EXPORT_ROLES.has(user?.role);
  const canView   = VIEW_ROLES.has(user?.role);
  const iid       = user?.institution_id;

  // FM7 BOUND-REOPEN-FM7-04: ERP export capability gate (INV-FM7-FOUND-01)
  const { isActive: integActive, statusOf: integStatus } = useIntegrationStatus();
  const erpActive = integActive("ERP");
  const [erpExporting, setErpExporting] = useState(false);

  const triggerErpExport = async () => {
    setErpExporting(true);
    try {
      const resp = await api.post("/finance/erp/export", {
        dataset: active === "overview" ? "payments" : active,
        provider: "test_erp",
      });
      toast.success(`ERP export queued — ${resp.data?.record_count ?? 0} records (${resp.data?.provider})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ERP export failed");
    } finally { setErpExporting(false); }
  };

  const [active, setActive] = useState(canExec ? "overview" : "assessments");

  // Nav definition (§13 hierarchy)
  const NAV = [
    {
      section: "overview",
      label: "Overview",
      icon: BarChart2,
      executive: true,
    },
    {
      section: "fees",
      label: "Fees & Assessments",
      icon: DollarSign,
      children: [{ section: "assessments", label: "Assessments" }],
    },
    {
      section: "invoices_payments",
      label: "Invoices & Payments",
      icon: Receipt,
      children: [
        { section: "invoices", label: "Invoices" },
        { section: "payments", label: "Payments" },
      ],
    },
    {
      section: "funding",
      label: "Funding",
      icon: BookOpen,
      children: [
        { section: "scholarships",   label: "Scholarships" },
        { section: "sponsorships",   label: "Sponsorships" },
        { section: "reimbursements", label: "Reimbursements" },
      ],
    },
    {
      section: "refunds_credits",
      label: "Refunds & Credits",
      icon: Scale,
      children: [
        { section: "refunds", label: "Refunds" },
        { section: "credits", label: "Credits" },
      ],
    },
    {
      section: "collections_group",
      label: "Collections",
      icon: AlertCircle,
      children: [
        { section: "collections", label: "Collections" },
        { section: "aging",       label: "Aging" },
      ],
    },
    {
      section: "export_centre",
      label: "Export Centre",
      icon: Download,
      export: true,
    },
  ];

  // Column definitions
  const COLS = {
    assessments: [
      { key: "id",                 label: "ID",       render: r => <span className="font-mono text-xs text-slate-500">{r.id?.slice(0,8)}…</span> },
      { key: "student_account_id", label: "Student",  render: r => <span className="font-mono text-xs">{r.student_account_id?.slice(0,8)}…</span> },
      { key: "fee_assessment_id",  label: "Assessment", render: r => r.fee_assessment_id ? <span className="font-mono text-xs">{r.fee_assessment_id?.slice(0,8)}…</span> : <span className="text-slate-600">—</span> },
      { key: "original_amount",    label: "Amount",   render: r => fmtAmt(r.original_amount, r.currency) },
      { key: "currency",           label: "CCY" },
      { key: "status",             label: "Status",   render: r => badge(r.status, STATUS_COLORS) },
      { key: "charge_date",        label: "Date",     render: r => fmtDate(r.charge_date) },
      { key: "description",        label: "Description", render: r => <span className="max-w-[160px] truncate block text-xs text-slate-400">{r.description || "—"}</span> },
    ],
    invoices: [
      { key: "id",                 label: "ID",       render: r => <span className="font-mono text-xs text-slate-500">{r.id?.slice(0,8)}…</span> },
      { key: "invoice_number",     label: "Invoice #" },
      { key: "student_account_id", label: "Student",  render: r => <span className="font-mono text-xs">{r.student_account_id?.slice(0,8)}…</span> },
      { key: "invoice_date",       label: "Date",     render: r => fmtDate(r.invoice_date) },
      { key: "due_date",           label: "Due",      render: r => fmtDate(r.due_date) },
      { key: "currency",           label: "CCY" },
      { key: "status",             label: "Status",   render: r => badge(r.status, STATUS_COLORS) },
    ],
    payments: [
      { key: "id",                 label: "ID",       render: r => <span className="font-mono text-xs text-slate-500">{r.id?.slice(0,8)}…</span> },
      { key: "payment_origin",     label: "Origin",   render: r => <span className="text-xs font-medium text-blue-400">{r.payment_origin}</span> },
      { key: "student_account_id", label: "Student",  render: r => <span className="font-mono text-xs">{r.student_account_id?.slice(0,8)}…</span> },
      { key: "amount",             label: "Amount",   render: r => fmtAmt(r.amount, r.currency) },
      { key: "currency",           label: "CCY" },
      { key: "payment_date",       label: "Date",     render: r => fmtDate(r.payment_date) },
      { key: "payment_method",     label: "Method" },
      { key: "status",             label: "Status",   render: r => badge(r.status, STATUS_COLORS) },
      { key: "reference_number",   label: "Ref #",    render: r => <span className="text-xs text-slate-400">{r.reference_number || "—"}</span> },
    ],
    scholarships: [
      { key: "id",                 label: "ID",       render: r => <span className="font-mono text-xs text-slate-500">{r.id?.slice(0,8)}…</span> },
      { key: "scheme_name",        label: "Scheme" },
      { key: "scholarship_type",   label: "Type",     render: r => <span className="text-xs text-slate-400">{r.scholarship_type || "—"}</span> },
      { key: "student_account_id", label: "Student",  render: r => <span className="font-mono text-xs">{r.student_account_id?.slice(0,8)}…</span> },
      { key: "coverage_basis",     label: "Basis" },
      { key: "amount",             label: "Amount",   render: r => fmtAmt(r.amount) },
      { key: "academic_period",    label: "Period",   render: r => <span className="text-xs text-slate-400">{r.academic_period || "—"}</span> },
      { key: "status",             label: "Status",   render: r => badge(r.status, STATUS_COLORS) },
    ],
    sponsorships: [
      { key: "id",                 label: "ID",       render: r => <span className="font-mono text-xs text-slate-500">{r.id?.slice(0,8)}…</span> },
      { key: "sponsor_name",       label: "Sponsor" },
      { key: "student_account_id", label: "Student",  render: r => <span className="font-mono text-xs">{r.student_account_id?.slice(0,8)}…</span> },
      { key: "coverage_amount",    label: "Coverage", render: r => fmtAmt(r.coverage_amount) },
      { key: "coverage_basis",     label: "Basis" },
      { key: "status",             label: "Status",   render: r => badge(r.status, STATUS_COLORS) },
    ],
    reimbursements: [
      { key: "id",                 label: "ID",       render: r => <span className="font-mono text-xs text-slate-500">{r.id?.slice(0,8)}…</span> },
      { key: "student_account_id", label: "Student",  render: r => <span className="font-mono text-xs">{r.student_account_id?.slice(0,8)}…</span> },
      { key: "eligibility_status", label: "Eligibility" },
      { key: "claim_status",       label: "Claim" },
      { key: "sanction_status",    label: "Sanction" },
      { key: "receipt_status",     label: "Receipt" },
      { key: "eligible_amount",    label: "Eligible", render: r => fmtAmt(r.eligible_amount) },
      { key: "claimed_amount",     label: "Claimed",  render: r => fmtAmt(r.claimed_amount) },
      { key: "sanctioned_amount",  label: "Sanctioned", render: r => fmtAmt(r.sanctioned_amount) },
      { key: "received_amount",    label: "Received", render: r => fmtAmt(r.received_amount) },
    ],
    refunds: [
      { key: "id",                 label: "ID",       render: r => <span className="font-mono text-xs text-slate-500">{r.id?.slice(0,8)}…</span> },
      { key: "student_account_id", label: "Student",  render: r => <span className="font-mono text-xs">{r.student_account_id?.slice(0,8)}…</span> },
      { key: "status",             label: "Status",   render: r => badge(r.status, STATUS_COLORS) },
      { key: "requested_amount",   label: "Amount",   render: r => fmtAmt(r.requested_amount) },
      { key: "reason_code",        label: "Reason" },
      { key: "approval_tier",      label: "Tier" },
      { key: "linked_payment_id",  label: "Payment Linked", render: r => r.linked_payment_id ? <span className="text-emerald-400 text-xs">Yes</span> : <span className="text-amber-400 text-xs">No</span> },
      { key: "created_at",         label: "Created",  render: r => fmtDate(r.created_at) },
    ],
    credits: [
      { key: "id",                 label: "ID",       render: r => <span className="font-mono text-xs text-slate-500">{r.id?.slice(0,8)}…</span> },
      { key: "student_account_id", label: "Student",  render: r => <span className="font-mono text-xs">{r.student_account_id?.slice(0,8)}…</span> },
      { key: "credit_origin",      label: "Origin",   render: r => {
        const cfg = { FM4A: "bg-blue-900/50 text-blue-300", FM5: "bg-violet-900/50 text-violet-300", OTHER: "bg-slate-700 text-slate-300" };
        return <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${cfg[r.credit_origin] || cfg.OTHER}`}>{r.credit_origin}</span>;
      }},
      { key: "reason_code",        label: "Reason Code" },
      { key: "amount",             label: "Amount",   render: r => fmtAmt(r.amount, r.currency) },
      { key: "currency",           label: "CCY" },
      { key: "status",             label: "Status",   render: r => badge(r.status, STATUS_COLORS) },
      { key: "credit_memo_date",   label: "Date",     render: r => fmtDate(r.credit_memo_date) },
    ],
    collections: [
      { key: "id",                       label: "ID",       render: r => <span className="font-mono text-xs text-slate-500">{r.id?.slice(0,8)}…</span> },
      { key: "student_account_id",       label: "Student",  render: r => <span className="font-mono text-xs">{r.student_account_id?.slice(0,8)}…</span> },
      { key: "status",                   label: "Status",   render: r => badge(r.status, STATUS_COLORS) },
      { key: "aging_provenance",         label: "Provenance", render: r => badge(r.aging_provenance, PROV_COLORS) },
      { key: "aging_bucket",             label: "Bucket" },
      { key: "collectible_amount_at_open", label: "Collectible", render: r => fmtAmt(r.collectible_amount_at_open) },
      { key: "provenance_unverified_amount", label: "Unverified Amt",
        render: r => r.provenance_unverified_amount > 0
          ? <span className="text-amber-400">{fmtAmt(r.provenance_unverified_amount)}</span>
          : <span className="text-slate-600">—</span>
      },
      { key: "hold_placed",  label: "Hold", render: r => r.hold_placed ? <span className="text-red-400 text-xs font-semibold">Yes</span> : <span className="text-slate-500 text-xs">No</span> },
      { key: "overdue_since", label: "Overdue Since", render: r => fmtDate(r.overdue_since) },
      { key: "created_at",   label: "Created", render: r => fmtDate(r.created_at) },
    ],
  };

  // Status filter pill
  const statusFilter = (field, options) => (filters, setFilters) => (
    <select data-testid="report-status-filter"
      value={filters[field] || ""}
      onChange={e => setFilters({ ...filters, [field]: e.target.value || undefined })}
      className="text-xs bg-slate-700 border border-slate-600 text-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500">
      <option value="">All Statuses</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const SECTION_TITLES = {
    overview: "Executive Finance Overview",
    assessments: "Fee Assessments Report",
    invoices: "Invoice Report",
    payments: "Payment & Receipt Report",
    scholarships: "Scholarship & Waiver Report",
    sponsorships: "Sponsorship Report",
    reimbursements: "Government Reimbursement Report",
    refunds: "Refund Report",
    credits: "Credit Report",
    collections: "Collections Report",
    aging: "Aging Report",
    export_centre: "Export Centre",
  };

  const renderContent = () => {
    switch (active) {
      case "overview":    return <OverviewSection iid={iid} canExec={canExec} />;
      case "assessments": return <GenericReport endpoint="assessments" cols={COLS.assessments} iid={iid} canExport={canExport} testid="assessments-report"
          extraFilters={statusFilter("status", ["DRAFT","FINALIZED","CANCELLED"])} />;
      case "invoices":    return <GenericReport endpoint="invoices" cols={COLS.invoices} iid={iid} canExport={canExport} testid="invoices-report"
          extraFilters={statusFilter("status", ["DRAFT","ISSUED","PAID","CANCELLED","OVERDUE"])} />;
      case "payments":    return <GenericReport endpoint="payments" cols={COLS.payments} iid={iid} canExport={canExport} testid="payments-report" />;
      case "scholarships":return <GenericReport endpoint="scholarships" cols={COLS.scholarships} iid={iid} canExport={canExport} testid="scholarships-report" />;
      case "sponsorships":return <GenericReport endpoint="sponsorships" cols={COLS.sponsorships} iid={iid} canExport={canExport} testid="sponsorships-report" />;
      case "reimbursements": return <GenericReport endpoint="reimbursements" cols={COLS.reimbursements} iid={iid} canExport={canExport} testid="reimbursements-report"
          extraFilters={statusFilter("sanction_status", ["PENDING","PARTIALLY_SANCTIONED","SANCTIONED","REJECTED"])} />;
      case "refunds":     return <GenericReport endpoint="refunds" cols={COLS.refunds} iid={iid} canExport={canExport} testid="refunds-report"
          extraFilters={statusFilter("status", ["INITIATED","UNDER_REVIEW","APPROVED","EXECUTED","REJECTED","CANCELLED"])} />;
      case "credits":     return <GenericReport endpoint="credits" cols={COLS.credits} iid={iid} canExport={canExport} testid="credits-report"
          extraFilters={statusFilter("status", ["PENDING","APPLIED","CANCELLED"])} />;
      case "collections": return <GenericReport endpoint="collections" cols={COLS.collections} iid={iid} canExport={canExport} testid="collections-report"
          extraFilters={statusFilter("status", ["MONITORING","OPEN","DEFAULTED","SUSPENDED","RESOLVED","CLOSED"])} />;
      case "aging":       return <AgingSection iid={iid} canExport={canExport} />;
      case "export_centre": return <ExportCentre iid={iid} canExport={canExport} />;
      default:            return null;
    }
  };

  if (!canView) return (
    <div className="flex items-center gap-3 p-8 bg-slate-800/40 rounded-xl border border-slate-700/50 m-6" data-testid="reports-no-access">
      <Lock className="h-6 w-6 text-slate-500 shrink-0" />
      <div>
        <p className="font-semibold text-slate-200">Finance Reports Access Restricted</p>
        <p className="text-sm text-slate-500 mt-1">Your role does not include <code className="text-xs">finance.reports.view</code>. Contact your Finance administrator.</p>
      </div>
    </div>
  );

  const isLeaf = (s) => !NAV.find(n => n.section === s || n.children?.some(c => c.section === s) === false);

  return (
    <div className="flex h-full" data-testid="finance-reports-workspace">
      {/* ── Left nav ── */}
      <nav className="w-52 shrink-0 border-r border-slate-700/50 bg-slate-900/50 flex flex-col py-3 gap-0.5 overflow-y-auto" data-testid="reports-nav">
        {NAV.map(item => {
          if (item.executive && !canExec) return null;
          if (item.children) {
            const groupActive = item.children.some(c => c.section === active);
            return (
              <div key={item.section}>
                <div className={`flex items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 ${groupActive ? "text-slate-300" : ""}`}>
                  <item.icon className="h-3.5 w-3.5" />{item.label}
                </div>
                {item.children.map(child => (
                  <button key={child.section} data-testid={`nav-${child.section}`}
                    onClick={() => setActive(child.section)}
                    className={`w-full text-left flex items-center gap-2 pl-7 pr-3 py-1.5 text-sm rounded-r-lg transition-colors ${
                      active === child.section ? "bg-blue-600/20 text-blue-300 border-l-2 border-blue-500 font-medium" : "text-slate-400 hover:text-slate-200 hover:bg-slate-700/40"
                    }`}>
                    <ChevronRight className="h-3 w-3 shrink-0" />{child.label}
                  </button>
                ))}
              </div>
            );
          }
          return (
            <button key={item.section} data-testid={`nav-${item.section}`}
              onClick={() => setActive(item.section)}
              className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded-r-lg transition-colors ${
                active === item.section ? "bg-blue-600/20 text-blue-300 border-l-2 border-blue-500 font-medium" : "text-slate-400 hover:text-slate-200 hover:bg-slate-700/40"
              }`}>
              <item.icon className="h-4 w-4 shrink-0" />{item.label}
            </button>
          );
        })}
      </nav>

      {/* ── Content panel ── */}
      <main className="flex-1 overflow-y-auto p-6" data-testid="reports-content">
        <div className="mb-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-white" data-testid="reports-section-title">
                {SECTION_TITLES[active] || active}
              </h1>
              <p className="text-xs text-slate-500 mt-1">
                Finance Reports · Report Version {active !== "export_centre" ? "1.0" : ""}
                {!canExport && active !== "overview" && active !== "export_centre" && (
                  <span className="ml-2 text-amber-500">(View only — your role cannot export)</span>
                )}
              </p>
            </div>
            {/* FM7 BOUND-REOPEN-FM7-04: Export to ERP surface
                Enabled ONLY when ERP integration is ACTIVE (INV-FM7-FOUND-01) */}
            {canExport && (
              <div data-testid="fm7-erp-export-surface">
                {erpActive ? (
                  <button
                    data-testid="export-to-erp-btn"
                    onClick={triggerErpExport}
                    disabled={erpExporting}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-50 transition-colors"
                  >
                    <Database className="h-3.5 w-3.5" />
                    {erpExporting ? "Exporting…" : "Export to ERP"}
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-slate-500 bg-slate-800/40 border border-slate-700/30 rounded-lg"
                    data-testid="erp-export-not-available">
                    <Database className="h-3.5 w-3.5 opacity-40" />
                    <span>
                      {integStatus("ERP") === "CONFIGURED"
                        ? "ERP configured — not yet active"
                        : "ERP not configured"}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {renderContent()}
      </main>
    </div>
  );
}
